/**
 * Live orders. Talks to the /liveorders group, which is the admin.php "Live
 * Orders" tab moved into Connect: what is in the kitchen now, per store, and
 * the two recovery actions (mark paid, force confirm) — every one audited.
 *
 * Non-namespaced like every other module here.
 */
const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Live orders", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => p.append(k, obj[k]));
  return p;
};

const state = () => ({
  listSeq: 0,
  summary: null, // { totals, per_store, as_of, hours }
  rows: [],
  total: 0,
  loaded: false,
  error: "",
  detail: null, // { order, items }
});

const mutations = {
  RESET_LIVEORDERS(s) { Object.assign(s, state()); },
  SET_LIVE_SUMMARY(s, v) { s.summary = v; s.loaded = true; s.error = ""; },
  SET_LIVE_ROWS(s, { rows, total }) { s.rows = rows || []; s.total = total || 0; },
  SET_LIVE_ERROR(s, m) { s.error = m; s.loaded = true; },
  SET_LIVE_DETAIL(s, v) { s.detail = v; },
};

const actions = {
  /** silent = a background poll: no global loader, no toast on failure. */
  async getLiveSummary({ commit }, { params, silent } = {}) {
    if (!silent) commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("liveorders/summary", { params });
      if (data.status) commit("SET_LIVE_SUMMARY", data);
      else if (!silent) { notifyError(commit, data.message); commit("SET_LIVE_ERROR", data.message || "The server refused the request."); }
    } catch (e) {
      if (!silent) {
        notifyError(commit, "Could not load live orders.");
        commit("SET_LIVE_ERROR", "Could not reach the live-orders service. It may not be installed on this server yet.");
      }
    } finally {
      if (!silent) commit("LOADING", { isActiveStatus: false });
    }
  },

  async getLiveOrders({ commit, state: s }, { params, silent } = {}) {
    const seq = ++s.listSeq;   // a slower earlier response must not overwrite a newer filter's rows
    try {
      const { data } = await window.$http.axios.get("liveorders/list", { params });
      if (seq !== s.listSeq) return;
      if (data.status) commit("SET_LIVE_ROWS", { rows: data.rows, total: data.total });
      else if (!silent) notifyError(commit, data.message);
    } catch (e) {
      if (!silent) notifyError(commit, "Could not load the order list.");
    }
  },

  async getLiveOrderDetail({ commit }, oID) {
    commit("SET_LIVE_DETAIL", null);
    try {
      const { data } = await window.$http.axios.get("liveorders/detail", { params: { oID } });
      if (data.status) commit("SET_LIVE_DETAIL", { order: data.order, items: data.items || [] });
      else notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not load the order.");
    }
  },

  async liveOrderMarkPaid({ commit }, { oID, reason }) {
    try {
      const { data } = await window.$http.axios.post("liveorders/mark-paid", form({ oID, reason }));
      if (data.status) { commit("NOTIFICATION_MESSAGE", { status: 1, title: "Live orders", message: "Order #" + oID + " marked as paid." }); return true; }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not mark the order paid."); return false; }
  },

  async liveOrderForceConfirm({ commit }, { oID, reason }) {
    try {
      const { data } = await window.$http.axios.post("liveorders/force-confirm", form({ oID, reason }));
      if (data.status) { commit("NOTIFICATION_MESSAGE", { status: 1, title: "Live orders", message: "Order #" + oID + " force-confirmed." }); return true; }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not confirm the order."); return false; }
  },
};

export default { state, mutations, actions };
